import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Plus, Edit3, Trash2, Save, X, Search, RefreshCw, FileDown, Loader2,
  DollarSign, TrendingUp, CalendarRange, Filter, CheckCircle, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TYPES = [
  { value: 'promotion', label: 'Promoción Playlist' },
  { value: 'music', label: 'Música / Track' },
  { value: 'pack', label: 'Pack' },
  { value: 'mastering', label: 'Mastering' },
  { value: 'other', label: 'Otro' },
];

const PAYMENT_METHODS = [
  { value: 'paypal', label: 'PayPal' },
  { value: 'card', label: 'Tarjeta' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'other', label: 'Otro' },
];

const labelOf = (list, value) => (list.find((x) => x.value === value)?.label) || value;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : '');

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  amount: '',
  sale_date: todayISO(),
  product_type: 'promotion',
  product_name: '',
  payment_method: 'paypal',
  customer_name: '',
  customer_email: '',
  notes: '',
});

const ManualSalesPanel = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    product_type: '',
    payment_method: '',
    search: '',
  });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [exporting, setExporting] = useState(false);

  const showMsg = (kind, text) => {
    setFeedback({ kind, text });
    setTimeout(() => setFeedback(null), 4000);
  };

  // ─── LOAD DATA ─────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/manual-sales`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/admin/manual-sales/stats/combined`, {
          params: { date_from: filters.date_from || undefined, date_to: filters.date_to || undefined },
          withCredentials: true,
        }),
      ]);
      setItems(listRes.data.items || []);
      setStats(statsRes.data);
    } catch (e) {
      console.error('manual-sales load', e);
      showMsg('error', 'No se pudieron cargar las ventas.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // ─── FORM ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (sale) => {
    setEditingId(sale.id);
    setForm({
      amount: String(sale.amount),
      sale_date: fmtDate(sale.sale_date) || todayISO(),
      product_type: sale.product_type,
      product_name: sale.product_name || '',
      payment_method: sale.payment_method,
      customer_name: sale.customer_name || '',
      customer_email: sale.customer_email || '',
      notes: sale.notes || '',
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    const payload = {
      ...form,
      amount: parseFloat(form.amount),
    };
    if (!payload.amount || payload.amount <= 0) {
      showMsg('error', 'Monto inválido.');
      return;
    }
    if (!payload.customer_name.trim()) {
      showMsg('error', 'El nombre del cliente es obligatorio.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/manual-sales/${editingId}`, payload, { withCredentials: true });
        showMsg('ok', 'Venta actualizada.');
      } else {
        await axios.post(`${API_URL}/api/admin/manual-sales`, payload, { withCredentials: true });
        showMsg('ok', 'Venta registrada.');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      load();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || 'Error al guardar.';
      showMsg('error', typeof detail === 'string' ? detail : 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta venta manual? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/manual-sales/${id}`, { withCredentials: true });
      showMsg('ok', 'Venta eliminada.');
      load();
    } catch (err) {
      showMsg('error', err?.response?.data?.detail || 'No se pudo eliminar.');
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      if (filters.product_type) params.set('product_type', filters.product_type);
      if (filters.payment_method) params.set('payment_method', filters.payment_method);
      params.set('include_online', 'true');

      const res = await axios.get(`${API_URL}/api/admin/manual-sales/export/pdf?${params.toString()}`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `og-music-ventas-${filters.date_from || 'all'}_${filters.date_to || 'all'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showMsg('ok', 'PDF descargado.');
    } catch (e) {
      console.error(e);
      showMsg('error', 'No se pudo generar el PDF.');
    } finally {
      setExporting(false);
    }
  };

  const totalFiltered = useMemo(
    () => items.reduce((s, x) => s + Number(x.amount || 0), 0),
    [items],
  );

  // ─── RENDER ────────────────────────────────────────────────────────────
  return (
    <div data-testid="manual-sales-panel" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Ventas Manuales (Offline)</h2>
          <p className="text-sm text-white/50 mt-1">
            Registra ventas históricas o realizadas fuera de la plataforma. Se suman al total general del dashboard.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-white/70"
            data-testid="manual-sales-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Actualizar
          </button>
          <button
            onClick={exportPDF}
            disabled={exporting}
            className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-white/70 disabled:opacity-50"
            data-testid="manual-sales-export-pdf"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Descargar PDF
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[#E8720C] hover:bg-[#cf6309] text-white rounded-xl text-xs font-semibold"
            data-testid="manual-sales-new"
          >
            <Plus className="w-4 h-4" /> Nueva venta
          </button>
        </div>
      </div>

      {/* Feedback */}
      {feedback && (
        <div
          data-testid="manual-sales-feedback"
          className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm border ${
            feedback.kind === 'ok'
              ? 'bg-green-500/10 border-green-500/30 text-green-300'
              : 'bg-red-500/10 border-red-500/30 text-red-300'
          }`}
        >
          {feedback.kind === 'ok' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {feedback.text}
        </div>
      )}

      {/* Combined stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            color="orange"
            icon={TrendingUp}
            label="TOTAL GENERAL"
            value={fmtMoney(stats.grand_total)}
            sub={`${stats.grand_count} transacciones`}
            testId="stat-grand-total"
          />
          <StatCard
            icon={DollarSign}
            label="Manuales (offline)"
            value={fmtMoney(stats.manual?.total)}
            sub={`${stats.manual?.count || 0} registros`}
            testId="stat-manual"
          />
          <StatCard
            icon={DollarSign}
            label="Online PayPal/Tarjeta"
            value={fmtMoney(stats.online_paypal_card?.total)}
            sub={`${stats.online_paypal_card?.count || 0} pedidos`}
            testId="stat-online"
          />
          <StatCard
            icon={DollarSign}
            label="Crypto Aprobadas"
            value={fmtMoney(stats.online_crypto?.total)}
            sub={`${stats.online_crypto?.count || 0} pedidos`}
            testId="stat-crypto"
          />
        </div>
      )}

      {/* Filters */}
      <div className="glass-card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <FilterInput
          icon={CalendarRange}
          type="date"
          label="Desde"
          value={filters.date_from}
          onChange={(v) => setFilters((f) => ({ ...f, date_from: v }))}
          testId="filter-date-from"
        />
        <FilterInput
          icon={CalendarRange}
          type="date"
          label="Hasta"
          value={filters.date_to}
          onChange={(v) => setFilters((f) => ({ ...f, date_to: v }))}
          testId="filter-date-to"
        />
        <FilterSelect
          icon={Filter}
          label="Tipo de producto"
          value={filters.product_type}
          options={[{ value: '', label: 'Todos' }, ...PRODUCT_TYPES]}
          onChange={(v) => setFilters((f) => ({ ...f, product_type: v }))}
          testId="filter-product-type"
        />
        <FilterSelect
          icon={Filter}
          label="Método de pago"
          value={filters.payment_method}
          options={[{ value: '', label: 'Todos' }, ...PAYMENT_METHODS]}
          onChange={(v) => setFilters((f) => ({ ...f, payment_method: v }))}
          testId="filter-payment-method"
        />
        <FilterInput
          icon={Search}
          type="text"
          label="Buscar"
          placeholder="Cliente, correo, producto..."
          value={filters.search}
          onChange={(v) => setFilters((f) => ({ ...f, search: v }))}
          testId="filter-search"
        />
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form
            onSubmit={submit}
            className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="manual-sales-form"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">
                {editingId ? 'Editar venta manual' : 'Registrar venta manual'}
              </h3>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); }}
                className="p-2 text-white/50 hover:text-white"
                data-testid="manual-sales-form-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Monto (USD)" required>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="ms-input"
                  required
                  data-testid="form-amount"
                />
              </FormField>
              <FormField label="Fecha de la venta" required>
                <input
                  type="date"
                  value={form.sale_date}
                  onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                  className="ms-input"
                  required
                  data-testid="form-sale-date"
                />
              </FormField>
              <FormField label="Tipo de producto" required>
                <select
                  value={form.product_type}
                  onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                  className="ms-input"
                  data-testid="form-product-type"
                >
                  {PRODUCT_TYPES.map((p) => (
                    <option key={p.value} value={p.value} style={{ backgroundColor: '#0f0f0f', color: '#fff' }}>{p.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Método de pago" required>
                <select
                  value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                  className="ms-input"
                  data-testid="form-payment-method"
                >
                  {PAYMENT_METHODS.map((p) => (
                    <option key={p.value} value={p.value} style={{ backgroundColor: '#0f0f0f', color: '#fff' }}>{p.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Nombre del cliente" required>
                <input
                  type="text"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className="ms-input"
                  required
                  placeholder="Nombre y apellido"
                  data-testid="form-customer-name"
                />
              </FormField>
              <FormField label="Correo del cliente">
                <input
                  type="email"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  className="ms-input"
                  placeholder="opcional"
                  data-testid="form-customer-email"
                />
              </FormField>
              <FormField label="Descripción del producto" full>
                <input
                  type="text"
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  className="ms-input"
                  placeholder="Ej: Playlist Promo Custom, Pack Hip-Hop, Mastering 5 tracks..."
                  data-testid="form-product-name"
                />
              </FormField>
              <FormField label="Notas internas" full>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="ms-input min-h-[80px]"
                  placeholder="Información adicional sólo visible para el admin"
                  data-testid="form-notes"
                />
              </FormField>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); }}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-sm text-white/70"
                data-testid="form-cancel"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-[#E8720C] hover:bg-[#cf6309] text-white rounded-xl text-sm font-semibold disabled:opacity-50"
                data-testid="form-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingId ? 'Guardar cambios' : 'Registrar venta'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      <div className="glass-card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <p className="text-xs text-white/50 uppercase tracking-wider">
            {items.length} registro{items.length !== 1 ? 's' : ''} · Subtotal: <span className="text-white font-semibold">{fmtMoney(totalFiltered)}</span>
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-white/40">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando...
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-white/40 text-sm">
            No hay ventas manuales registradas con estos filtros.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Producto</th>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Pago</th>
                  <th className="text-right px-4 py-3">Monto</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`row-${s.id}`}>
                    <td className="px-4 py-3 text-white/80 whitespace-nowrap">{fmtDate(s.sale_date)}</td>
                    <td className="px-4 py-3 text-white">
                      <div className="font-medium">{s.customer_name}</div>
                      {s.customer_email && (
                        <div className="text-xs text-white/40">{s.customer_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/70 max-w-[280px] truncate">{s.product_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-wider bg-white/5 ring-1 ring-white/10 text-white/70 px-2 py-0.5 rounded-full">
                        {labelOf(PRODUCT_TYPES, s.product_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/70">{labelOf(PAYMENT_METHODS, s.payment_method)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-white">{fmtMoney(s.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(s)}
                          className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg"
                          data-testid={`edit-${s.id}`}
                          title="Editar"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(s.id)}
                          className="p-2 text-red-400/70 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                          data-testid={`delete-${s.id}`}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Local style for inputs to match admin aesthetic */}
      <style>{`
        .ms-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 0.75rem;
          color: #fff;
          padding: 0.6rem 0.85rem;
          font-size: 0.875rem;
          outline: none;
        }
        .ms-input:focus { border-color: rgba(232,114,12,0.6); background: rgba(255,255,255,0.06); }
        .ms-input::placeholder { color: rgba(255,255,255,0.30); }
        /* Make dropdown options readable on all OS (white text on dark menu) */
        .ms-input option,
        .ms-input optgroup {
          background-color: #0f0f0f;
          color: #ffffff;
        }
        .ms-input option:checked,
        .ms-input option:hover {
          background-color: #E8720C;
          color: #ffffff;
        }
      `}</style>
    </div>
  );
};

// ─── Small subcomponents ──────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color = 'white', testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] text-white/40 uppercase tracking-wider">{label}</span>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
        color === 'orange' ? 'bg-[#E8720C]/20' : 'bg-white/10'
      }`}>
        <Icon className={`w-4 h-4 ${color === 'orange' ? 'text-[#E8720C]' : 'text-white/60'}`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-white/40 mt-1">{sub}</p>}
  </div>
);

const FilterInput = ({ icon: Icon, label, type, value, onChange, placeholder, testId }) => (
  <label className="block">
    <span className="text-[10px] text-white/40 uppercase tracking-wider flex items-center gap-1 mb-1">
      <Icon className="w-3 h-3" /> {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="ms-input"
      data-testid={testId}
    />
  </label>
);

const FilterSelect = ({ icon: Icon, label, value, options, onChange, testId }) => (
  <label className="block">
    <span className="text-[10px] text-white/40 uppercase tracking-wider flex items-center gap-1 mb-1">
      <Icon className="w-3 h-3" /> {label}
    </span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="ms-input"
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} style={{ backgroundColor: '#0f0f0f', color: '#fff' }}>{o.label}</option>
      ))}
    </select>
  </label>
);

const FormField = ({ label, required, full, children }) => (
  <label className={`block ${full ? 'sm:col-span-2' : ''}`}>
    <span className="text-xs text-white/60 mb-1 block">
      {label}{required && <span className="text-[#E8720C] ml-1">*</span>}
    </span>
    {children}
  </label>
);

export default ManualSalesPanel;
